import { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import DownloadModalTables from "../../components/DownloadModalTables";
import PortalPopup from "../../components/PortalPopup";
import DateFilter from "../../components/DateFilter";
import LawFirm from "../../components/LawFirm";
import NavigationDropdown from "../../components/NavigationDropdown";
import NavigationPanel from "../../components/NavigationPanel";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import Breadcrumb from "../../components/Breadcrumb";
import CTAs from "../../components/CTAs";
import Footer from "../../components/Footer.js";
import { getCurrentUser } from "../../utils/getCurrentUser";

/* ── Styles ── */
const pageStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

  html, body { background: rgb(241, 245, 249) !important; }

  .lpd-root * { box-sizing: border-box; }
  .lpd-root {
    font-family: 'DM Sans', sans-serif;
    background: rgb(241, 245, 249) !important;
    min-height: 100vh;
  }
  .lpd-root.bg-neutrals-neutral-50,
  .lpd-root.bg-white { background: rgb(241, 245, 249) !important; }

  /* Cards */
  .lpd-card {
    background: #ffffff;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    position: relative;
    overflow: hidden;
  }
  .lpd-card:hover { box-shadow: 0 4px 24px rgba(0,0,0,0.09); }

  .lpd-metric-card { cursor: default; }
  .lpd-metric-card:hover { transform: translateY(-3px); }
  .lpd-metric-card .accent-bar {
    position: absolute; top: 0; left: 0; right: 0;
    height: 4px; border-radius: 16px 16px 0 0;
  }

  /* Table card — no transform on hover */
  .lpd-table-card { cursor: default; }
  .lpd-table-card:hover { transform: none; }

  /* Table */
  .lpd-table { width: 100%; border-collapse: collapse; }
  .lpd-table thead tr { background: #F8FAFC; }
  .lpd-table th {
    padding: 11px 16px; text-align: left;
    font-size: 11px; font-weight: 700; color: #94A3B8;
    text-transform: uppercase; letter-spacing: 0.07em;
    border-bottom: 1px solid #E2E8F0;
  }
  .lpd-table td {
    padding: 14px 16px; font-size: 14px; color: #1E293B;
    border-bottom: 1px solid #F1F5F9; vertical-align: middle;
  }
  .lpd-table tbody tr { transition: background 0.12s; }
  .lpd-table tbody tr:hover { background: #F8FAFC; }
  .lpd-table tbody tr:last-child td { border-bottom: none; }

  /* Tab row */
  .lpd-tab-row {
    display: inline-flex;
    background: #F1F5F9;
    border-radius: 8px;
    padding: 3px; gap: 2px;
  }
  .lpd-tab {
    padding: 6px 16px; border-radius: 6px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; color: #64748B;
    cursor: pointer; border: none; background: transparent;
    transition: background 0.15s, color 0.15s;
    white-space: nowrap;
  }
  .lpd-tab:hover { background: #ffffff; color: #0F172A; }
  .lpd-tab.lpd-active { background: #ffffff; color: #2563EB; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

  /* Search */
  .lpd-search {
    display: flex; align-items: center; gap: 8px;
    background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px;
    padding: 9px 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    width: 280px; transition: border-color 0.15s, box-shadow 0.15s;
  }
  .lpd-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
  .lpd-search input {
    border: none; outline: none; background: transparent;
    font-family: 'DM Sans', sans-serif; font-size: 14px; color: #0F172A; width: 100%;
  }
  .lpd-search input::placeholder { color: #94A3B8; }

  /* Buttons */
  .lpd-btn {
    display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px;
    background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #1E293B;
    cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .lpd-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
  .lpd-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .lpd-btn-primary {
    display: inline-flex; align-items: center; gap: 8px; padding: 9px 18px;
    background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%);
    border: none; border-radius: 10px;
    font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700; color: #ffffff;
    cursor: pointer; box-shadow: 0 2px 8px rgba(37,99,235,0.3);
    transition: opacity 0.15s, transform 0.15s;
  }
  .lpd-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }

  /* Count pill */
  .lpd-count-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; background: #EFF6FF; border-radius: 20px;
    font-size: 12px; font-weight: 700; color: #2563EB;
  }

  /* Page btn */
  .lpd-page-btn {
    display: inline-flex; align-items: center; gap: 6px; padding: 7px 16px;
    background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A;
    cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .lpd-page-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
  .lpd-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Section labels */
  .lpd-section-title { font-size: 16px; font-weight: 700; color: #0F172A; }
  .lpd-section-sub   { font-size: 13px; color: #64748B; margin-top: 2px; }
`;

const LPDPage = () => {
  const navigate = useNavigate();
  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] = useState(false);
  const [isDateFilterOpen, setDateFilterOpen] = useState(false);
  const [isLawFirmOpen, setLawFirmOpen] = useState(false);
  const [isLawFirm1Open, setLawFirm1Open] = useState(false);
  const [isLawFirm2Open, setLawFirm2Open] = useState(false);
  const [selectedDateTimeRange, setSelectedDateTimeRange] = useState("");
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [debtors, setDebtors] = useState([]);
  const [userId, setUserId] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;
  const [totalSubmitted, setTotalSubmitted] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedDebtorId, setSelectedDebtorId] = useState(null);
  const user = getCurrentUser();
  const [settledLPDCount, setSettledLPDCount] = useState(0);
  const [pendingLPDCount, setPendingLPDCount] = useState(0);
  const token = localStorage.getItem("authToken");

  const openDownloadModalTables  = useCallback(() => setDownloadModalTablesOpen(true),  []);
  const closeDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(false), []);
  const openDateFilter           = useCallback(() => setDateFilterOpen(true),  []);
  const closeDateFilter          = useCallback(() => setDateFilterOpen(false), []);
  const openLawFirm  = useCallback((debtorId) => { setSelectedDebtorId(debtorId); setLawFirmOpen(true); }, []);
  const closeLawFirm  = useCallback(() => setLawFirmOpen(false),  []);
  const openLawFirm1  = useCallback(() => setLawFirm1Open(true),  []);
  const closeLawFirm1 = useCallback(() => setLawFirm1Open(false), []);
  const openLawFirm2  = useCallback(() => setLawFirm2Open(true),  []);
  const closeLawFirm2 = useCallback(() => setLawFirm2Open(false), []);
  const openNavigationDropdown  = useCallback(() => setNavigationDropdownOpen(true),  []);
  const closeNavigationDropdown = useCallback(() => setNavigationDropdownOpen(false), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);

  useEffect(() => {
    const storedUserId = user?.id;
    if (storedUserId) setUserId(storedUserId);

    const fetchTotalSubmitted = async () => {
      try {
        const response = await fetch(`${API_URL}/api/lpd/lpd_data/${storedUserId}`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success && Array.isArray(data.details)) {
          const settledStatuses = ["resolved", "settled", "paid", "completed", "closed"];
          const settledCount = data.details.filter((item) => settledStatuses.includes(item.status?.toLowerCase())).length;
          setSettledLPDCount(settledCount);
          setPendingLPDCount(data.details.length - settledCount);
          setTotalSubmitted(data.count);
          setDebtors(data.details.map((item) => ({
            id: item._id,
            name: item.name,
            status: item.status || "Pending",
            amount: `£${item.amount}`,
            dueDate: new Date(item.dueDate).toLocaleDateString("en-GB"),
            action: "Contact law firm",
          })));
        }
      } catch (error) {
        console.error("Error fetching total submitted count:", error);
      }
    };
    fetchTotalSubmitted();
  }, []);

  const onCTAsContainerClick = useCallback(() => navigate("/lpd2"), [navigate]);

  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const handleDateTimeSelect = (dateRange) => setSelectedDateTimeRange(dateRange);

  const filteredDebtors = activeTab === "all"
    ? debtors
    : debtors.filter((d) => d.status.toLowerCase() === activeTab.toLowerCase());

  const searchedDebtors = filteredDebtors.filter((d) =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.amount.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.dueDate.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* Status badge colours */
  const statusStyle = (status) => {
    switch ((status || "").toLowerCase()) {
      case "overdue":  return { background: "#FEF2F2", color: "#DC2626" };
      case "sent":     return { background: "#EFF6FF", color: "#2563EB" };
      case "pending":  return { background: "#FFFBEB", color: "#D97706" };
      case "resolved": return { background: "#F0FDF4", color: "#16A34A" };
      default:         return { background: "#F1F5F9", color: "#64748B" };
    }
  };

  const indexOfLastItem  = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDebtors   = searchedDebtors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages       = Math.ceil(searchedDebtors.length / itemsPerPage);

  const handlePreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
  const handleNextPage     = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };

  const metricCards = [
    {
      label: "Total LPD's generated",
      value: totalSubmitted,
      accentBar: "#7C3AED",
      iconBg: "#F5F3FF",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8ZM7 13h6M7 17h4" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Settled LPD's",
      value: settledLPDCount,
      accentBar: "#059669",
      iconBg: "#ECFDF5",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="m7.75 12 2.83 2.83 5.67-5.66" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Pending LPD's",
      value: pendingLPDCount,
      accentBar: "#F59E0B",
      iconBg: "#FFFBEB",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 9v5M12 21.41H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12v-.01Z" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M11.995 17h.009" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  const tabs = [
    { key: "all",      label: "View all"  },
    { key: "sent",     label: "Sent"      },
    { key: "pending",  label: "Pending"   },
    { key: "overdue",  label: "Overdue"   },
    { key: "Resolved", label: "Settled"   },
  ];

  /* ─────────────────────── RENDER ─────────────────────── */
  return (
    <>
      <style>{pageStyles}</style>

      <div
        className="lpd-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div style={{ position: "absolute", top: 136, width: "100%", padding: "0 96px 64px", display: "flex", flexDirection: "column", gap: 28 }}>

          {/* ── Page header ── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", paddingBottom: 24, borderBottom: "1px solid #E2E8F0" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>Late Payment Demand (LPD)</h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>All information regarding your LPD.</p>
            </div>
            <button className="lpd-btn-primary" onClick={onCTAsContainerClick}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M6 12h12M12 18V6" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              New LPD
            </button>
          </div>

          {/* ── 3 metric cards ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
            {metricCards.map((card) => (
              <div key={card.label} className="lpd-card lpd-metric-card" style={{ padding: 24 }}>
                <div className="accent-bar" style={{ background: card.accentBar }}/>
                <div style={{ marginTop: 8 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: card.iconBg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, border: "1px solid #E2E8F0" }}>
                    {card.icon}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                    {card.label}
                  </div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 28, fontWeight: 500, color: "#0F172A", lineHeight: 1.1 }}>
                    {card.value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Table card ── */}
          <div className="lpd-card lpd-table-card" style={{ overflow: "hidden" }}>

            {/* Table header row */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="lpd-section-title">Generated LPD's</span>
                <span className="lpd-count-pill">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563EB", display: "inline-block" }}/>
                  {filteredDebtors.length === 1 ? "1 entry" : `${filteredDebtors.length || "No"} entries`}
                </span>
              </div>
              <button className="lpd-btn" onClick={openDownloadModalTables}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download
              </button>
            </div>

            {/* Tabs + search/filter row */}
            <div style={{ padding: "12px 24px", borderBottom: "1px solid #F1F5F9", background: "#FAFBFD", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div className="lpd-tab-row">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`lpd-tab${activeTab === tab.key ? " lpd-active" : ""}`}
                    onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="lpd-search">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search your LPD…"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  />
                </div>
                <button className="lpd-btn" onClick={openDateFilter}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M3 7h18M6 12h12M10 17h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Filter
                </button>
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table className="lpd-table">
                <thead>
                  <tr>
                    {["Debtor's Details", "Status", "Amount Due", "Due Date"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentDebtors.length > 0 ? (
                    currentDebtors.map((debtor) => {
                      const badge = statusStyle(debtor.status);
                      return (
                        <tr key={debtor.id}>
                          <td style={{ fontWeight: 600, color: "#0F172A" }}>{debtor.name}</td>
                          <td>
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              padding: "3px 10px", borderRadius: 20,
                              fontSize: 12, fontWeight: 600,
                              background: badge.background, color: badge.color,
                            }}>
                              ● {debtor.status}
                            </span>
                          </td>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                            {(debtor.amount || "0").toLocaleString?.("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? debtor.amount}
                          </td>
                          <td style={{ color: "#64748B", fontSize: 13 }}>{debtor.dueDate}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ textAlign: "center", color: "#94A3B8", padding: 40 }}>No entries found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", borderTop: "1px solid #E2E8F0", background: "#FAFBFD" }}>
              <button className="lpd-page-btn" onClick={handlePreviousPage} disabled={currentPage === 1}>
                ← Previous
              </button>
              <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>
                {searchedDebtors.length === 0 ? "No results" : `Page ${currentPage} of ${totalPages}`}
              </span>
              <button className="lpd-page-btn" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0}>
                Next →
              </button>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* ── Modals — all unchanged ── */}
      {isDownloadModalTablesOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeDownloadModalTables}>
          <DownloadModalTables onClose={closeDownloadModalTables} tableData={searchedDebtors}/>
        </PortalPopup>
      )}
      {isDateFilterOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeDateFilter}>
          <DateFilter onClose={closeDateFilter} onDateTimeSelect={handleDateTimeSelect}/>
        </PortalPopup>
      )}
      {isLawFirmOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLawFirm}>
          <LawFirm onClose={closeLawFirm} debtorId={selectedDebtorId} formType="lpd"/>
        </PortalPopup>
      )}
      {isLawFirm1Open && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLawFirm1}>
          <LawFirm onClose={closeLawFirm1} debtorId={selectedDebtorId} formType="lpd"/>
        </PortalPopup>
      )}
      {isLawFirm2Open && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLawFirm2}>
          <LawFirm onClose={closeLawFirm2} debtorId={selectedDebtorId} formType="lpd"/>
        </PortalPopup>
      )}
      {isNavigationDropdownOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeNavigationDropdown}>
          <NavigationDropdown onClose={closeNavigationDropdown}/>
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation}/>
        </PortalPopup>
      )}
    </>
  );
};

export default LPDPage;